import http from '../utils/http';
import { ccUtils as utils } from '@/common/Utils';
import { pick } from 'lodash';
import { notifySuccess, notifyError } from './common';
import { Promise as bbPromise } from 'bluebird';

const defaultState = {
    entityName: 'user',
    baseUrl: 'api/users/',
    user: {},
    userScopes: [],
    readHelpUrl: '/user-guide/guide/user-detail.html',
    editHelpUrl: '/user-guide/guide/user-edit.html',
    addHelpUrl: '/user-guide/guide/user-add.html'
};

export const mutations = {
    setUser(state, value) {
        if (value) {
            state.user = value;
        } else {
            state.user = {};
        }
    },
    setUserScopes(state, value) {
        state.userScopes = Array.isArray(value) ? value : [];
    }
};

export const actions = {
    fetchUser({ commit, state }, { userId }) {
        console.log('calling fetchUser', userId);
        return http
            .get(`${state.baseUrl}${userId}`, {
                notifyRequestPending: true
            })
            .then(res => {
                const { userScopes, ...rest } = res.data;
                commit('setUser', rest);
                commit('setUserScopes', userScopes);
            });
    },
    resetUser({ commit }) {
        console.log('calling resetUser');
        commit('setUser', {});
        commit('setUserScopes', []);
    },
    createUser({ state }, { userInfo, userScopes }) {
        // will send a post API request to create new user
        // userObject structure should be validated in the UI
        console.log('calling createUser API');
        let scopes = [];
        let username = '';
        if (userScopes && userScopes.length) {
            scopes = userScopes.map(scope => {
                return {
                    mso: scope.mso,
                    partner: scope.partner,
                    venue: scope.venue,
                    withGrant: scope.withGrant,
                    userScopeRoles: scope.roles.map(role => {
                        return {
                            roleId: role.roleId,
                            withGrant: role.withGrant
                        };
                    })
                };
            });
        }
        if (!userInfo.xocAuth) {
            username = userInfo.email;
        } else {
            username = userInfo.username;
        }
        const userObject = {
            ...userInfo,
            username,
            userScopes: scopes
        };
        return http.post(state.baseUrl, userObject, {
            notifyRequestPending: true,
            successMessage: 'User created successfully',
            notifyError: true
        });
    },
    updateUserEntity({ dispatch }, { oldUserInfo, newUserInfo, userScopes }) {
        // will send patch API request to update existing user
        // due to the API design the update user will make multiple calls to
        // save changes to the user object along with scopes/roles
        console.log('calling updateUserEntity', newUserInfo);
        const successMessage = 'User data has been updated successfuly';
        const failMessage = 'Error occured while saving this user...';
        dispatch('notifier/requestPending', null, { root: true });
        let actions = [];
        // check if need to update userProfile fields
        if (!newUserInfo.xocAuth) {
            newUserInfo.username = newUserInfo.email;
        }
        const userDiff = utils.diffObject(oldUserInfo, newUserInfo);
        if (Object.keys(userDiff).length) {
            const updateUser = pick(newUserInfo, [
                ...Object.keys(userDiff),
                'id'
            ]);
            actions.push({
                action: 'updateUser',
                payload: { user: updateUser }
            });
        }

        if (userScopes) {
            actions.push(...getUserScopesUpdates(newUserInfo, userScopes));
        }
        return bbPromise
            .each(actions, item => dispatch(item.action, item.payload))
            .then(() => {
                notifySuccess(dispatch, successMessage);
            })
            .catch(error => {
                notifyError(dispatch, failMessage, error);
            })
            .finally(() => {
                dispatch('notifier/reset', null, { root: true });
            });
    },
    deleteUser({ state }, { userId, notifyConfig }) {
        // delete the user object
        console.log('calling deleteUser');
        const defaultConfig = {
            notifyRequestPending: true,
            notifySuccess: true,
            notifyError: true,
            successMessage: 'User deleted successfully',
            errorMessage: 'Delete user failed due to API issue',
            ...notifyConfig
        };
        return http.delete(`${state.baseUrl}${userId}`, null, {
            ...defaultConfig
        });
    },
    sendResetPasswordEmail({ state }, { userId, url, mso }) {
        console.log('calling sendUserResetPasswordEmail');
        return http.post(
            `${state.baseUrl}${userId}/sendResetPassword?url=${url}&mso=${mso}`,
            null,
            {
                notifyError: true,
                successMessage:
                    'Reset password email has been sent successfully.'
            }
        );
    },
    updateUser({ state }, { user }) {
        return http.patch(
            `${state.baseUrl}${user.id}`,
            { ...user },
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    },
    createScope({ state }, { scope, userId }) {
        // we can pass the scope object along with userScopeRoles to the API
        return http.post(
            `${state.baseUrl}${userId}/userScopes/`,
            { ...scope },
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    },
    updateScope({ state }, { scope, userId }) {
        return http.patch(
            `${state.baseUrl}${userId}/userScopes/${scope.id}`,
            { ...scope },
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    },
    deleteScope({ state }, { scope, userId }) {
        return http.delete(
            `${state.baseUrl}${userId}/userScopes/`,
            [scope.id],
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    },
    createRole({ state }, { role, userId, scopeId }) {
        return http.post(
            `${state.baseUrl}${userId}/userScopes/${scopeId}/roles/`,
            { ...role },
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    },
    updateRole({ state }, { role, userId, scopeId }) {
        return http.patch(
            `${state.baseUrl}${userId}/userScopes/${scopeId}/roles/${role.id}`,
            { ...role },
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    },
    deleteRole({ state }, { role, userId, scopeId }) {
        return http.delete(
            `${state.baseUrl}${userId}/userScopes/${scopeId}/roles/`,
            [role.id],
            {
                notifyRequestPending: false,
                notifyError: false,
                notifySuccess: false
            }
        );
    }
};

export default {
    namespaced: true,
    state: defaultState,
    mutations,
    actions
};

const getUserScopesUpdates = (userInfo, userScopes) => {
    let actions = [];
    if (userScopes && userScopes.added && userScopes.added.length) {
        userScopes.added.forEach(scope => {
            actions.push({
                action: 'createScope',
                payload: {
                    scope,
                    userId: userInfo.id
                }
            });
        });
    }
    if (userScopes && userScopes.updated && userScopes.updated.length) {
        userScopes.updated.forEach(scope => {
            if (scope.isDirty) {
                actions.push({
                    action: 'updateScope',
                    payload: {
                        scope,
                        userId: userInfo.id
                    }
                });
            }
            scope.userScopeRoles.forEach(role => {
                actions.push(getRoleUpdates(userInfo, scope, role));
            });
        });
    }
    if (userScopes && userScopes.deleted && userScopes.deleted.length) {
        userScopes.deleted.forEach(scope => {
            actions.push({
                action: 'deleteScope',
                payload: {
                    scope,
                    userId: userInfo.id
                }
            });
        });
    }
    return actions;
};

const getRoleUpdates = (userInfo, scope, role) => {
    let action;
    if (role.isNew) {
        action = {
            action: 'createRole',
            payload: {
                role,
                userId: userInfo.id,
                scopeId: scope.id
            }
        };
    } else if (role.isDirty) {
        action = {
            action: 'updateRole',
            payload: {
                role,
                userId: userInfo.id,
                scopeId: scope.id
            }
        };
    } else if (role.isDeleted) {
        action = {
            action: 'deleteRole',
            payload: {
                role,
                userId: userInfo.id,
                scopeId: scope.id
            }
        };
    }
    return action;
};
